import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { Star, X, Gift, Heart, ExternalLink } from "lucide-react";
import { api, vibrate } from "@/lib/api";

const TRUSTPILOT_URL = process.env.REACT_APP_TRUSTPILOT_URL;

export default function NpsPrompt({ trigger = "dashboard" }) {
  const [open, setOpen] = useState(false);
  const [rating, setRating] = useState(0);
  const [hover, setHover] = useState(0);
  const [step, setStep] = useState("rate"); // rate | positive | negative | done
  const [testimonial, setTestimonial] = useState("");
  const [consent, setConsent] = useState(true);
  const [feedback, setFeedback] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [bonus, setBonus] = useState(false);

  useEffect(() => {
    if (localStorage.getItem("dmd_nps_done") === "1") return;
    api.get("/reviews/status")
      .then((r) => { if (r.data.eligible) setOpen(true); })
      .catch(() => {});
  }, []);

  const dismiss = () => {
    setOpen(false);
    // Remember the dismissal so we don't nag on every dashboard visit.
    localStorage.setItem("dmd_nps_done", "1");
  };

  const pickRating = (n) => {
    vibrate();
    setRating(n);
    setStep(n >= 4 ? "positive" : "negative");
  };

  const submit = async () => {
    setSubmitting(true);
    try {
      const res = await api.post("/reviews", {
        rating,
        testimonial: step === "positive" ? testimonial : "",
        consent: step === "positive" ? consent : false,
        feedback: step === "negative" ? feedback : "",
        trigger,
      });
      setBonus(!!res.data.bonus_granted);
      setStep("done");
      localStorage.setItem("dmd_nps_done", "1");
      if (res.data.bonus_granted) {
        toast.success("🎁 3 jours de Pro offerts, merci !");
      } else {
        toast.success("Merci pour ton retour 💜");
      }
    } catch (e) {
      toast.error("Oups, réessaie dans un instant");
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[70] flex items-end sm:items-center justify-center bg-black/70 backdrop-blur-sm p-4"
        data-testid="nps-modal"
      >
        <motion.div
          initial={{ y: 40, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 40, opacity: 0 }}
          transition={{ type: "spring", stiffness: 300, damping: 28 }}
          className="w-full max-w-md rounded-3xl bg-zinc-900 border border-zinc-800 p-6 relative"
        >
          <button
            data-testid="nps-close"
            onClick={dismiss}
            className="absolute top-4 right-4 text-zinc-500 hover:text-zinc-300"
          >
            <X size={18} />
          </button>

          {step === "rate" && (
            <div className="text-center">
              <div className="w-12 h-12 mx-auto rounded-2xl bg-pink-500/15 flex items-center justify-center text-pink-400 mb-3">
                <Heart size={22} />
              </div>
              <h3 className="font-display text-xl font-black">Tu kiffes DocLove ?</h3>
              <p className="text-sm text-zinc-400 mt-1">Note ton expérience en 1 tap 👇</p>
              <div className="flex justify-center gap-1.5 mt-5" data-testid="nps-stars">
                {[1, 2, 3, 4, 5].map((n) => (
                  <button
                    key={n}
                    data-testid={`nps-star-${n}`}
                    onMouseEnter={() => setHover(n)}
                    onMouseLeave={() => setHover(0)}
                    onClick={() => pickRating(n)}
                    className="p-1 active:scale-90 transition-transform"
                  >
                    <Star
                      size={34}
                      className={(hover || rating) >= n ? "text-amber-400 fill-amber-400" : "text-zinc-700"}
                    />
                  </button>
                ))}
              </div>
            </div>
          )}

          {step === "positive" && (
            <div>
              <div className="flex justify-center gap-1 mb-3">
                {[1, 2, 3, 4, 5].map((n) => (
                  <Star key={n} size={20} className={rating >= n ? "text-amber-400 fill-amber-400" : "text-zinc-700"} />
                ))}
              </div>
              <h3 className="font-display text-xl font-black text-center">Merci 💜</h3>
              <p className="text-sm text-zinc-400 mt-1 text-center">
                Laisse un petit mot — et reçois <span className="text-emerald-400 font-semibold">3 jours de Pro offerts</span>.
              </p>
              <textarea
                data-testid="nps-testimonial"
                value={testimonial}
                onChange={(e) => setTestimonial(e.target.value)}
                placeholder="Ce que DocLove a changé pour toi…"
                rows={3}
                maxLength={280}
                className="mt-4 w-full rounded-xl bg-zinc-950 border border-zinc-800 p-3 text-sm text-zinc-100 placeholder-zinc-600 focus:border-pink-500 outline-none resize-none"
              />
              <label className="mt-3 flex items-start gap-2 text-xs text-zinc-400 cursor-pointer">
                <input
                  type="checkbox"
                  data-testid="nps-consent"
                  checked={consent}
                  onChange={(e) => setConsent(e.target.checked)}
                  className="mt-0.5 accent-pink-500"
                />
                J'autorise DocLove à afficher mon avis (prénom + note) sur son site.
              </label>
              <button
                data-testid="nps-submit-positive"
                onClick={submit}
                disabled={submitting}
                className="mt-4 w-full rounded-full bg-pink-500 text-white font-bold py-3 hover:bg-pink-600 active:scale-95 transition disabled:opacity-50"
              >
                {submitting ? "Envoi…" : "Envoyer & recevoir 3 jours Pro 🎁"}
              </button>
            </div>
          )}

          {step === "negative" && (
            <div>
              <h3 className="font-display text-xl font-black">Aïe, on peut faire mieux</h3>
              <p className="text-sm text-zinc-400 mt-1">
                Qu'est-ce qui te bloque ou te déçoit ? (retour privé, lu par l'équipe)
              </p>
              <textarea
                data-testid="nps-feedback"
                value={feedback}
                onChange={(e) => setFeedback(e.target.value)}
                placeholder="Dis-nous tout…"
                rows={4}
                maxLength={800}
                className="mt-4 w-full rounded-xl bg-zinc-950 border border-zinc-800 p-3 text-sm text-zinc-100 placeholder-zinc-600 focus:border-pink-500 outline-none resize-none"
              />
              <button
                data-testid="nps-submit-negative"
                onClick={submit}
                disabled={submitting}
                className="mt-4 w-full rounded-full bg-zinc-100 text-zinc-900 font-bold py-3 hover:bg-white active:scale-95 transition disabled:opacity-50"
              >
                {submitting ? "Envoi…" : "Envoyer mon retour"}
              </button>
            </div>
          )}

          {step === "done" && (
            <div className="text-center py-4">
              <div className="w-14 h-14 mx-auto rounded-2xl bg-emerald-500/15 flex items-center justify-center text-emerald-400 mb-3">
                {bonus ? <Gift size={26} /> : <Heart size={26} />}
              </div>
              <h3 className="font-display text-xl font-black">
                {bonus ? "3 jours de Pro débloqués !" : "Merci pour ton retour 💜"}
              </h3>
              <p className="text-sm text-zinc-400 mt-1">
                {bonus ? "Profite de toutes les fonctionnalités Pro." : "On lit chaque message pour améliorer DocLove."}
              </p>
              {rating >= 4 && TRUSTPILOT_URL && (
                <a
                  data-testid="nps-trustpilot"
                  href={TRUSTPILOT_URL}
                  target="_blank"
                  rel="noreferrer"
                  onClick={() => vibrate()}
                  className="mt-5 w-full flex items-center justify-center gap-2 rounded-full bg-emerald-500 text-white font-bold py-3 hover:bg-emerald-600 active:scale-95 transition"
                >
                  <Star size={16} className="fill-white" /> Partage ton avis sur Trustpilot <ExternalLink size={15} />
                </a>
              )}
              <button
                data-testid="nps-done"
                onClick={() => setOpen(false)}
                className={`w-full rounded-full font-bold py-3 active:scale-95 transition ${rating >= 4 && TRUSTPILOT_URL ? "mt-3 bg-zinc-800 text-zinc-200 hover:bg-zinc-700" : "mt-5 bg-pink-500 text-white hover:bg-pink-600"}`}
              >
                Continuer
              </button>
            </div>
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
